import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { usePostManagementHook } from '../../../../hooks/admin/post/use-post';
import { useHiddenPostManagementHook } from '../../../../hooks/admin/hiddenPost/use-hidden-post';
import {
  fetchPostsApi,
  fetchInvalidPostsAPI,
  handleAdminPostApi,
  handleInvalidPostApi,
  recoverPostApi,
} from '../../../../api/admin/post.api';
import { IPostModel } from '../../../../models';
import { promptTotp } from '../../../../utils/admin-stepup';
import { Check, RotateCcw, TriangleAlert } from 'lucide-react';
import { AdminButton, AdminPageHeader, AdminPillTabs } from '../../ui';
import { AdminPostModCard } from '../post-moderation/AdminPostParts';

// Interfaces
interface PostManagementHookParams {
  pageNumber: number;
  isRefetch: boolean;
  status?: string;
}

// Mock hooks and APIs (replace with actual implementations)
const mockUsePostManagementHook = ({
  pageNumber,
  isRefetch,
  status,
}: PostManagementHookParams) => {
  return { data: [] as IPostModel[] };
};
const mockUseHiddenPostManagementHook = ({
  pageNumber,
  isRefetch,
}: PostManagementHookParams) => {
  return { data: [] as IPostModel[] };
};
const mockFetchPostsApi = async (page: number, status: string) => {
  return { data: { data: [] as IPostModel[] } };
};
const mockFetchInvalidPostsAPI = async (page: number) => {
  return { data: { data: [] as IPostModel[] } };
};
const mockHandleAdminPostApi = async (postId: string, status: string, reason?: string, totpCode?: string) => {};
const mockHandleInvalidPostApi = async (postId: string, status: string) => {};
const mockRecoverPostApi = async (postId: string) => {};

// Use actual hooks/APIs or mocks based on environment
const usePostManagement = usePostManagementHook || mockUsePostManagementHook;
const useHiddenPostManagement =
  useHiddenPostManagementHook || mockUseHiddenPostManagementHook;
const fetchPosts = fetchPostsApi || mockFetchPostsApi;
const fetchInvalidPosts = fetchInvalidPostsAPI || mockFetchInvalidPostsAPI;
const handleAdminPost = handleAdminPostApi || mockHandleAdminPostApi;
const handleInvalidPost = handleInvalidPostApi || mockHandleInvalidPostApi;
const recoverPost = recoverPostApi || mockRecoverPostApi;

const ManagementPost: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>('Pending');
  const [invalidSubTab, setInvalidSubTab] = useState<string>('Invalid');
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [isRefetch, setIsRefetch] = useState<boolean>(false);
  const [invalidPosts, setInvalidPosts] = useState<IPostModel[]>([]);
  const [lengthPostUnCheck, setLengthPostUnCheck] = useState<number>(0);
  const [lengthPostInvalid, setLengthPostInvalid] = useState<number>(0);
  const [lengthPostApprove, setLengthPostApprove] = useState<number>(0);
  const [lengthPostNotApprove, setLengthPostNotApprove] = useState<number>(0);
  const [lengthPostPublic, setLengthPostPublic] = useState<number>(0);
  const [lengthPostHidden, setLengthPostHidden] = useState<number>(0);

  const { data: pendingData } = usePostManagement({
    pageNumber,
    isRefetch,
    status: 'Uncheck',
  });
  const { data: approvedData } = usePostManagement({
    pageNumber,
    isRefetch,
    status: 'Approved',
  });
  const { data: publicData } = usePostManagement({
    pageNumber,
    isRefetch,
    status: 'Public',
  });
  const { data: hiddenData } = useHiddenPostManagement({
    pageNumber,
    isRefetch,
  });

  const getAllPostUnCheck = async () => {
    try {
      const { data } = await fetchPosts(1, 'Uncheck');
      setLengthPostUnCheck(data.data.length);
    } catch {
      // Handle error
    }
  };

  const getAllPostInvalid = async () => {
    try {
      const { data } = await fetchInvalidPosts(1);
      setLengthPostInvalid(data.data.length);
      if (invalidSubTab === 'Invalid') {
        setInvalidPosts(data.data);
      }
    } catch {
      // Handle error
    }
  };

  const getAllPostApprove = async () => {
    try {
      const { data } = await fetchPosts(1, 'Approved');
      setLengthPostApprove(data.data.length);
    } catch {
      // Handle error
    }
  };

  const getAllPostNotApprove = async () => {
    try {
      const { data } = await fetchPosts(1, 'NotApproved');
      setLengthPostNotApprove(data.data.length);
    } catch {
      // Handle error
    }
  };

  const getAllPostPublic = async () => {
    try {
      const { data } = await fetchPosts(1, 'Public');
      setLengthPostPublic(data.data.length);
    } catch {
      // Handle error
    }
  };

  const getAllPostHidden = async () => {
    try {
      const { data } = await fetchPosts(1, 'Hidden'); // Adjust API if needed
      setLengthPostHidden(data.data.length);
    } catch {
      // Handle error
    }
  };

  const handlePost = async (postId: string, newStatus: string) => {
    // Máy chủ bắt mã Authenticator khi duyệt bài (PostAdminController); trước đây trang này không gửi mã nên luôn
    // báo "Thất bại" (rà soát 28/10). Trang /admin/post/pending đã hỏi mã theo cách này.
    const totp = promptTotp('duyệt bài viết');
    if (!totp) return;
    try {
      await handleAdminPost(postId, newStatus, undefined, totp);
      setIsRefetch(!isRefetch);
      toast.success('Thành công');
      await Promise.all([
        getAllPostUnCheck(),
        getAllPostInvalid(),
        getAllPostApprove(),
        getAllPostNotApprove(),
        getAllPostPublic(),
      ]);
    } catch {
      toast.error('Thất bại');
    }
  };

  const handleInvalidPost = async (postId: string, newStatus: string) => {
    try {
      await handleInvalidPostApi(postId, newStatus);
      setInvalidPosts(invalidPosts.filter((post) => post.id !== postId));
      toast.success('Thành công');
      await Promise.all([
        getAllPostUnCheck(),
        getAllPostInvalid(),
        getAllPostApprove(),
        getAllPostNotApprove(),
        getAllPostPublic(),
      ]);
      if (activeTab === 'Pending') {
        setIsRefetch(!isRefetch);
      }
    } catch {
      toast.error('Thất bại');
    }
  };

  const handleRecoverPost = async (postId: string) => {
    try {
      await recoverPost(postId);
      setIsRefetch(!isRefetch);
      toast.success('Thành công');
      await getAllPostHidden();
    } catch {
      toast.error('Thất bại');
    }
  };

  useEffect(() => {
    getAllPostUnCheck();
    getAllPostInvalid();
    getAllPostApprove();
    getAllPostNotApprove();
    getAllPostPublic();
    getAllPostHidden();
  }, []);

  const highlightBannedWords = (
    content: string,
    bannedWords: string
  ): string => {
    let highlightedContent = content || '';
    if (bannedWords) {
      bannedWords.split(',').forEach((word) => {
        if (word) {
          const regex = new RegExp(`\\b${word}\\b`, 'gi');
          highlightedContent = highlightedContent.replace(
            regex,
            `<span style="background-color: yellow;">${word}</span>`
          );
        }
      });
    }
    return highlightedContent;
  };

  const renderPosts = (posts: IPostModel[], tabType: string) => {
    return (
      <div className="adm-mod-grid">
        {posts?.map((post, index) => (
          <AdminPostModCard
            key={post.id || index}
            post={post}
            highlightWords={
              tabType === 'Invalid' && post.detectedBannedWords
                ? post.detectedBannedWords.split(',')
                : []
            }
            extra={
              tabType === 'Invalid' && post.detectedBannedWords ? (
                <p className="adm-strip adm-strip--danger" role="status">
                  <TriangleAlert size={20} className="adm-strip__icon" />
                  <span>
                    <strong>Những từ vi phạm:</strong> {post.detectedBannedWords}
                  </span>
                </p>
              ) : undefined
            }
            actions={
              <>
                {tabType === 'Pending' && (
                  <>
                    <AdminButton size={32} onClick={() => handlePost(post.id, 'Public')}>
                      Công khai
                    </AdminButton>
                    <AdminButton size={32} variant="outline" onClick={() => handlePost(post.id, 'Approved')}>
                      Riêng tư
                    </AdminButton>
                    <AdminButton size={32} variant="danger-soft" onClick={() => handlePost(post.id, 'NotApproved')}>
                      Không duyệt
                    </AdminButton>
                  </>
                )}
                {tabType === 'Invalid' && (
                  <>
                    <AdminButton size={32} variant="danger-soft" onClick={() => handleInvalidPost(post.id, 'NotApproved')}>
                      Không duyệt
                    </AdminButton>
                    <AdminButton size={32} variant="success" icon={<Check size={14} />} onClick={() => handleInvalidPost(post.id, 'Approved')}>
                      Duyệt
                    </AdminButton>
                  </>
                )}
                {tabType === 'Hidden' && (
                  <AdminButton size={32} icon={<RotateCcw size={14} />} onClick={() => handleRecoverPost(post.id)}>
                    Phục hồi bài viết
                  </AdminButton>
                )}
              </>
            }
          />
        ))}
      </div>
    );
  };

  const tabCount = (tab: string) =>
    `${
      tab === 'Pending'
        ? lengthPostUnCheck
        : tab === 'Approved'
        ? lengthPostApprove
        : tab === 'Public'
        ? lengthPostPublic
        : lengthPostInvalid + lengthPostHidden
    } bài viết`;

  return (
    <div className="adm-page">
      <AdminPageHeader
        title="Quản lý bài viết"
        description="Xem toàn bộ bài viết và đổi chế độ hiển thị của từng bài."
      />
      <AdminPillTabs
        ariaLabel="Lọc bài viết"
        value={activeTab}
        onChange={(tab) => {
          setActiveTab(tab);
          if (tab !== 'Invalid') setInvalidSubTab('Invalid');
        }}
        items={[
          { key: 'Pending', label: 'Chờ duyệt', count: tabCount('Pending') },
          { key: 'Approved', label: 'Đã duyệt', count: tabCount('Approved') },
          { key: 'Public', label: 'Công khai', count: tabCount('Public') },
          { key: 'Invalid', label: 'Vi phạm', count: tabCount('Invalid') },
        ]}
      />
      {activeTab === 'Invalid' && (
        <AdminPillTabs
          ariaLabel="Loại bài vi phạm"
          value={invalidSubTab}
          onChange={setInvalidSubTab}
          items={[
            { key: 'Invalid', label: 'Vi phạm', count: `${lengthPostInvalid} bài viết` },
            { key: 'Hidden', label: 'Ẩn', count: `${lengthPostHidden} bài viết` },
            { key: 'Reported', label: 'Báo cáo', count: '0 bài viết' },
          ]}
        />
      )}
      {activeTab === 'Pending' && renderPosts(pendingData, 'Pending')}
      {activeTab === 'Approved' && renderPosts(approvedData, 'Approved')}
      {activeTab === 'Public' && renderPosts(publicData, 'Public')}
      {activeTab === 'Invalid' && invalidSubTab === 'Invalid' && renderPosts(invalidPosts, 'Invalid')}
      {activeTab === 'Invalid' && invalidSubTab === 'Hidden' && renderPosts(hiddenData, 'Hidden')}
      {activeTab === 'Invalid' && invalidSubTab === 'Reported' && (
        <div className="adm-panel">
          <p>Chưa có bài viết báo cáo</p>
        </div>
      )}
    </div>
  );
};

export default ManagementPost;
